import moment from 'moment-timezone';
import cardStatusEnum from '@/utils/cardStatusEnum';

type NightCardValidationValue = string | {
  nightRiderCardStatus?: string;
  status?: string;
  validationStatus?: string;
  blockedTo?: string;
  blockedUntilDate?: string;
  untilDate?: string;
  date?: string;
  validTo?: string;
  expirationDate?: string;
  cardNumber?: string;
  [key: string]: unknown;
} | null | undefined;

type TranslateFn = (key: string, values?: Record<string, unknown>) => string;

function isExpiredAtBookingStatus(status: string | null): boolean {
  return status === cardStatusEnum.EXPIRED_ON_BOOKING_CREATION;
}

function isExpiredBeforeTravelStatus(status: string | null): boolean {
  return status === cardStatusEnum.EXPIRED;
}

function isBlockedStatus(status: string | null): boolean {
  return status === cardStatusEnum.SUSPENDED;
}

function resolveValidationDate(status: string | null, value: Record<string, any>): string | null {
  if (isBlockedStatus(status)) {
    return value.blockedTo
      || value.blockedUntilDate
      || value.untilDate
      || value.date
      || null;
  }
  if (isExpiredAtBookingStatus(status) || isExpiredBeforeTravelStatus(status)) {
    return value.validTo
      || value.expirationDate
      || value.date
      || null;
  }
  return value.date
    || value.validTo
    || value.blockedTo
    || value.expirationDate
    || value.blockedUntilDate
    || value.untilDate
    || null;
}

export function parseNightCardValidationResult(value: NightCardValidationValue) {
  if (!value) {
    return { status: null as string | null, date: null as string | null };
  }
  if (typeof value === 'string') {
    return { status: value, date: null as string | null };
  }
  const status = value.nightRiderCardStatus
    || value.status
    || value.validationStatus
    || null;
  return {
    status,
    date: resolveValidationDate(status, value),
  };
}

export function getNightRiderCardValidationEntries(priceResponse: Record<string, any> | null | undefined) {
  const detailsMap = priceResponse?.nightRiderCardsValidationResultWithDetails;
  if (!detailsMap || typeof detailsMap !== 'object') {
    return [] as Array<[string, any]>;
  }

  return Object.entries(detailsMap).map(([key, detail]: [string, any]) => [
    detail?.cardNumber || key,
    detail,
  ]) as Array<[string, any]>;
}

export function getValidationStatus(validationResult: NightCardValidationValue) {
  return parseNightCardValidationResult(validationResult).status;
}

export function getValidationDate(validationResult: NightCardValidationValue) {
  return parseNightCardValidationResult(validationResult).date;
}

export function isNightCardDiscountEligible(validationResult: NightCardValidationValue) {
  return getValidationStatus(validationResult) === cardStatusEnum.ACTIVE;
}

export function isNightCardExpiredAtBooking(validationResult: NightCardValidationValue) {
  return isExpiredAtBookingStatus(getValidationStatus(validationResult));
}

export function isNightCardExpiredBeforeTravel(validationResult: NightCardValidationValue) {
  return isExpiredBeforeTravelStatus(getValidationStatus(validationResult));
}

export function isNightCardBlocked(validationResult: NightCardValidationValue) {
  return isBlockedStatus(getValidationStatus(validationResult));
}

export function isNightCardOutOfCommune(validationResult: NightCardValidationValue) {
  return getValidationStatus(validationResult) === cardStatusEnum.NO_STOPS_IN_COMMUNE_BORDER;
}

export function isNightCardWrongPhoneNumber(validationResult: NightCardValidationValue) {
  return getValidationStatus(validationResult) === cardStatusEnum.WRONG_PHONE_NUMBER;
}

export function isNightCardInvalidStatus(validationResult: NightCardValidationValue) {
  return getValidationStatus(validationResult) === cardStatusEnum.INVALID;
}

export function isNightCardFieldInvalid(validationResult: NightCardValidationValue) {
  const status = getValidationStatus(validationResult);
  if (!status || isNightCardDiscountEligible(validationResult)) {
    return false;
  }
  return status !== cardStatusEnum.NO_STOPS_IN_COMMUNE_BORDER;
}

const FIELD_ERROR_MESSAGE_KEYS: Record<string, string> = {
  [cardStatusEnum.EXPIRED]: 'nightRider.nightCardExpiresBeforeTravelError',
  [cardStatusEnum.EXPIRED_ON_BOOKING_CREATION]: 'nightRider.nightCardExpiredError',
  [cardStatusEnum.SUSPENDED]: 'nightRider.nightCardBlockedError',
  [cardStatusEnum.INVALID]: 'nightRider.nightCardInvalidError',
  [cardStatusEnum.WRONG_PHONE_NUMBER]: 'nightRider.nightCardInvalidError',
};

const DISCLAIMER_MESSAGE_KEYS: Record<string, string> = {
  [cardStatusEnum.EXPIRED]: 'nightRider.disclaimerExpiredNightCard',
  [cardStatusEnum.EXPIRED_ON_BOOKING_CREATION]: 'nightRider.disclaimerExpiresBeforeTravel',
  [cardStatusEnum.SUSPENDED]: 'nightRider.disclaimerBlockedNightCard',
};

export function getFieldErrorMessageKey(validationResult: NightCardValidationValue) {
  const status = getValidationStatus(validationResult);
  return (status && FIELD_ERROR_MESSAGE_KEYS[status]) || null;
}

export function getDisclaimerMessageKey(validationResult: NightCardValidationValue) {
  const status = getValidationStatus(validationResult);
  return (status && DISCLAIMER_MESSAGE_KEYS[status]) || null;
}

export function formatNightCardDate(date: string | null | undefined, locale?: string) {
  if (!date) {
    return '';
  }
  const parsed = moment(date);
  if (!parsed.isValid()) {
    return String(date);
  }
  return parsed.locale(locale || 'en').format('MMM DD, YYYY');
}

export function resolveFieldErrorMessage(validationResult: NightCardValidationValue, translate?: TranslateFn) {
  const messageKey = getFieldErrorMessageKey(validationResult);
  if (!messageKey || !translate) {
    return '';
  }
  return translate(messageKey);
}

export function resolveDisclaimerMessage(validationResult: NightCardValidationValue, translate?: TranslateFn, locale?: string) {
  const messageKey = getDisclaimerMessageKey(validationResult);
  if (!messageKey || !translate) {
    return '';
  }
  return translate(messageKey, {
    date: formatNightCardDate(getValidationDate(validationResult), locale),
  });
}

export function resolveMainPassengerCardNumber({
  isAnotherPassenger,
  linkedNightRiderCardNumber,
  mainPassengerNightCard,
}: {
  isAnotherPassenger?: boolean;
  linkedNightRiderCardNumber?: string;
  mainPassengerNightCard?: string;
}) {
  if (!isAnotherPassenger) {
    return linkedNightRiderCardNumber || '';
  }

  return (mainPassengerNightCard || '').trim();
}

export function getAdditionalNightCardFieldsCount(adults: number) {
  return Math.max(0, adults - 1);
}

export function isMainPassengerCardReadyForValidation(cardNumber?: string | null) {
  return !!(cardNumber && cardNumber.length >= 7);
}

export function resolveBackendValidFromValidationResult(validationResult: NightCardValidationValue) {
  if (isNightCardDiscountEligible(validationResult)) {
    return true;
  }
  if (isNightCardOutOfCommune(validationResult)) {
    return null;
  }
  if (isNightCardFieldInvalid(validationResult)) {
    return false;
  }
  return false;
}
